import React from "react";
var __jsx = React.createElement;
import { UnstyledButton } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faFileLines, faPaperclip, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { formatDateTime } from "@/utils/format";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { StatusPill } from "../status-pill";
import { getEncounterDocumentCount, getEncounterDocuments, getEncounterLabs, getEncounterTypeLabel } from "./encounter-utils";
var CELL = "px-4 py-4 align-top text-style-body-small text-sds-base-default";

/** Expandable table rows for one encounter, revealing labs and documents. */
export function EncounterRecordRow(_ref) {
  var encounter = _ref.encounter,
    onOpenDocument = _ref.onOpenDocument,
    isOpening = _ref.isOpening,
    onAddNote = _ref.onAddNote;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    isExpanded = _useState[0],
    setIsExpanded = _useState[1];
  var title = getEncounterTypeLabel(encounter.encounter_type);
  var docCount = getEncounterDocumentCount(encounter);
  var documents = getEncounterDocuments(encounter);
  var labs = getEncounterLabs(encounter);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
    "data-testid": "encounter-row-".concat(encounter.fhir_id),
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.ENCOUNTER_ROW, {
        encounterId: encounter.fhir_id
      });
      setIsExpanded(function (prev) {
        return !prev;
      });
    },
    className: "cursor-pointer border-t border-sds-base-default hover:bg-sds-brand-tertiary ".concat(isExpanded ? "bg-sds-brand-tertiary" : "")
  }, /*#__PURE__*/React.createElement("td", {
    className: "".concat(CELL, " w-10")
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: isExpanded ? faChevronDown : faChevronRight,
    className: "h-3.5 w-3.5 text-sds-base-secondary"
  })), /*#__PURE__*/React.createElement("td", {
    className: CELL
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-2"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default"
  }, title, encounter.encounter_class ? ", ".concat(encounter.encounter_class) : ""), /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-base text-sds-base-tertiary"
  }, formatDateTime(encounter.start_date)), /*#__PURE__*/React.createElement(StatusPill, {
    status: encounter.status
  }))), /*#__PURE__*/React.createElement("td", {
    className: CELL
  }, encounter.reason && encounter.reason.length > 0 ? encounter.reason.join(", ") : "—"), /*#__PURE__*/React.createElement("td", {
    className: CELL
  }, encounter.provider || t("not_assigned")), /*#__PURE__*/React.createElement("td", {
    className: CELL
  }, encounter.location || t("not_specified")), /*#__PURE__*/React.createElement("td", {
    className: CELL
  }, docCount > 0 ? /*#__PURE__*/React.createElement("span", {
    className: "inline-flex items-center gap-2 rounded-sds-200 border border-sds-base-default px-3 py-1.5 text-style-label-base text-sds-base-secondary"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faPaperclip,
    className: "h-3.5 w-3.5 text-sds-brand-default"
  }), docCount) : /*#__PURE__*/React.createElement("span", {
    className: "text-sds-base-tertiary"
  }, "\u2014")), /*#__PURE__*/React.createElement("td", {
    className: CELL
  }, onAddNote ? /*#__PURE__*/React.createElement(UnstyledButton, {
    type: "button",
    "data-testid": "encounter-add-note-".concat(encounter.fhir_id),
    "aria-label": t("add_note_attachment", {
      defaultValue: "Add note / attachment"
    }),
    title: t("add_note_attachment", {
      defaultValue: "Add note / attachment"
    }),
    onClick: function onClick(event) {
      event.stopPropagation();
      trackEvent(ANALYTICS_EVENTS.ENCOUNTER_ADD_NOTE, {
        encounterId: encounter.fhir_id
      });
      onAddNote(encounter);
    },
    className: "flex h-7 w-7 items-center justify-center rounded-sds-200 border border-sds-base-default text-sds-brand-default hover:bg-sds-brand-tertiary"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faPlus,
    className: "h-3.5 w-3.5"
  })) : null)), isExpanded ? /*#__PURE__*/React.createElement("tr", {
    className: "border-t border-sds-base-default bg-sds-base-secondary"
  }, /*#__PURE__*/React.createElement("td", {
    colSpan: 7,
    className: "px-9 py-5"
  }, /*#__PURE__*/React.createElement("div", {
    className: "grid grid-cols-1 gap-8 sm:grid-cols-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-tertiary"
  }, t("labs_ordered")), labs.length ? /*#__PURE__*/React.createElement("div", {
    className: "flex flex-wrap gap-2"
  }, labs.map(function (lab) {
    return /*#__PURE__*/React.createElement("span", {
      key: lab,
      className: "rounded-sds-200 border border-sds-base-default bg-sds-base-default px-3 py-1.5 text-style-label-base text-sds-base-secondary"
    }, lab);
  })) : /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-base text-sds-base-tertiary"
  }, t("no_labs_ordered"))), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-tertiary"
  }, t("documents")), documents.length ? /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-2"
  }, documents.map(function (document) {
    return /*#__PURE__*/React.createElement(UnstyledButton, {
      key: document.resourceId,
      type: "button",
      "data-testid": "encounter-document-".concat(document.resourceId),
      disabled: isOpening,
      onClick: function onClick(event) {
        event.stopPropagation();
        trackEvent(ANALYTICS_EVENTS.ENCOUNTER_DOCUMENT, {
          encounterId: encounter.fhir_id,
          documentId: document.resourceId
        });
        onOpenDocument(document, encounter);
      },
      className: "flex items-center gap-2 text-style-body-small text-sds-brand-default hover:underline disabled:opacity-50"
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faFileLines,
      className: "h-3.5 w-3.5 flex-shrink-0"
    }), /*#__PURE__*/React.createElement("span", {
      className: "truncate"
    }, document.name));
  })) : /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-base text-sds-base-tertiary"
  }, t("no_documents_available")))), /*#__PURE__*/React.createElement("div", {
    className: "mt-4 border-t border-sds-base-default pt-4 text-style-label-base text-sds-base-tertiary"
  }, t("encounter_id"), " ", encounter.fhir_id, " \xB7 ", t("last_updated_label"), ":", " ", formatDateTime(encounter.updated_at)))) : null);
}